import clsx from "clsx";
import { isArray, isString } from "lodash-es";
import { ButtonHTMLAttributes, ElementType, forwardRef, ReactElement, useMemo } from "react";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { useElement } from "@/hooks/common/use-element";
import { DefaultComponentPropsWith, PlainObject } from "@/interfaces/common";
import { isElement } from "@/utils";
import { Icon, IconName } from "../Icon";

export type ButtonVariant = "primary" | "secondary" | "tertiary" | "destructive" | "unstyled" | "text";

export type ColorsName = "white" | "primary" | "secondary" | "error";

interface Classes {
  base: string;
  disabledBase: string;
  variant: { [Key in ButtonVariant]: string };
  text: { [Key in ButtonVariant]: string };
  hover: { [Key in ButtonVariant]: string };
  groupHover: { [Key in ButtonVariant]: string };
  loadingColor: { [Key in ButtonVariant]: ColorsName };
}

export interface ButtonProps extends DefaultComponentPropsWith<ButtonHTMLAttributes<HTMLButtonElement>> {
  variant?: ButtonVariant;
  disabled?: boolean;
  isLoading?: boolean;
  leading?: ReactElement | IconName;
  trailing?: ReactElement | IconName;
  loadingColor?: ColorsName;
  full?: boolean;
  fit?: boolean;
  as?: ElementType;
  className?: string;
  textClassName?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant = "primary",
    disabled = false,
    isLoading = false,
    leading,
    trailing,
    loadingColor,
    type = "button",
    className,
    textClassName,
    full = false,
    fit,
    ...attributes
  },
  ref,
) {
  const textBase = "!w-fit !min-h-[auto] font-medium text-sm";

  const classes: Classes = {
    base: "flex items-center justify-center transition-all ease-in-out duration-300 w-full max-w-button font-medium gap-2 py-2 px-6",
    disabledBase: clsx("pointer-events-none !text-body opacity-50", {
      "!bg-button-primary-locked !opacity-100": variant === "primary",
    }),
    variant: {
      primary: "bg-primary active:bg-button-primary-pressed",
      secondary: "outline outline-2 outline-input-border text-primary active:bg-button-secondary-pressed bg-white",
      tertiary: "",
      destructive: "outline outline-2 outline-input-border text-primary active:bg-button-destructive-pressed bg-white",
      text: clsx(textBase, "font-bold text-body"),
      unstyled: "",
    },
    text: {
      primary: clsx(textBase, "text-white"),
      secondary: clsx(textBase, "text-primary"),
      tertiary: clsx(textBase, "font-bold text-primary"),
      destructive: clsx(textBase, "text-error"),
      text: clsx(textBase, {
        "!text-primary [&_.icon]:text-primary": leading || trailing,
      }),
      unstyled: clsx(textBase),
    },
    hover: {
      primary: "",
      secondary: "hover:bg-input-border",
      tertiary: "",
      destructive: "hover:bg-button-destructive-hover",
      text: "group",
      unstyled: "hover:opacity-75",
    },
    groupHover: {
      primary: "",
      secondary: "",
      tertiary: "",
      destructive: "",
      text: "",
      unstyled: "",
    },
    loadingColor: {
      primary: loadingColor ?? "white",
      secondary: loadingColor ?? "secondary",
      tertiary: loadingColor ?? "secondary",
      destructive: loadingColor ?? "error",
      text: loadingColor ?? "white",
      unstyled: loadingColor ?? "white",
    },
  };

  const [Leading, Trailing] = useElement<PlainObject>(
    { size: "1.5rem" },
    isElement(leading) ? leading : <Icon className={classes.text[variant]} name={leading} />,
    isElement(trailing) ? trailing : <Icon className={classes.text[variant]} name={trailing} />,
  );

  const isStringContent = useMemo(() => {
    return isString(children) || (isArray(children) && children.every(isString));
  }, [children]);

  return (
    <button
      {...attributes}
      ref={ref}
      type={type}
      disabled={isLoading || disabled}
      className={clsx(
        classes.base,
        classes.variant[variant],
        {
          [classes.disabledBase]: disabled,
          [classes.hover[variant]]: !disabled && !isLoading,
          "max-w-none": full,
          "!w-fit max-w-none": fit,
        },
        className,
      )}
    >
      {Leading && !isLoading && <Leading />}
      {!isLoading ? (
        <span
          className={clsx(textClassName, {
            [classes.text[variant]]: isStringContent,
            [classes.groupHover[variant]]: !disabled && !isLoading,
            contents: !isStringContent,
          })}
        >
          {children}
        </span>
      ) : (
        <LoadingSpinner color={classes.loadingColor[variant]} />
      )}
      {Trailing && !isLoading && <Trailing />}
    </button>
  );
});
